import type { Bounds } from '../../../../core/shapes/models/ShapeViewModel';
import type { AlignmentGuideCandidate, AlignmentLine, AlignmentXAnchor, AlignmentYAnchor } from '../../models/AlignmentGuides';
import type { TransformHandle, TransformSnapInput, TransformSnapOutput } from '../../models/TransformSnap';

type XTarget = { anchor: AlignmentXAnchor; value: number; shapeId: string };
type YTarget = { anchor: AlignmentYAnchor; value: number; shapeId: string };

const xAnchorRank: Record<AlignmentXAnchor, number> = { center: 0, left: 1, right: 2 };
const yAnchorRank: Record<AlignmentYAnchor, number> = { middle: 0, top: 1, bottom: 2 };

const clampSize = (n: number): number => Math.max(1, n);

const getXTargetsForCandidate = (c: AlignmentGuideCandidate): XTarget[] => {
  const left = c.bounds.x;
  const center = c.bounds.x + c.bounds.width / 2;
  const right = c.bounds.x + c.bounds.width;
  return [
    { anchor: 'left', value: left, shapeId: c.shapeId },
    { anchor: 'center', value: center, shapeId: c.shapeId },
    { anchor: 'right', value: right, shapeId: c.shapeId },
  ];
};

const getYTargetsForCandidate = (c: AlignmentGuideCandidate): YTarget[] => {
  const top = c.bounds.y;
  const middle = c.bounds.y + c.bounds.height / 2;
  const bottom = c.bounds.y + c.bounds.height;
  return [
    { anchor: 'top', value: top, shapeId: c.shapeId },
    { anchor: 'middle', value: middle, shapeId: c.shapeId },
    { anchor: 'bottom', value: bottom, shapeId: c.shapeId },
  ];
};

const handleAffectsX = (h: TransformHandle): 'left' | 'right' | null => {
  if (!h) return null;
  if (h === 'left' || h === 'top-left' || h === 'bottom-left') return 'left';
  if (h === 'right' || h === 'top-right' || h === 'bottom-right') return 'right';
  return null;
};

const handleAffectsY = (h: TransformHandle): 'top' | 'bottom' | null => {
  if (!h) return null;
  if (h === 'top' || h === 'top-left' || h === 'top-right') return 'top';
  if (h === 'bottom' || h === 'bottom-left' || h === 'bottom-right') return 'bottom';
  return null;
};

type BestX = { target: XTarget; diff: number } | null;
type BestY = { target: YTarget; diff: number } | null;

const pickBetterX = (a: BestX, b: { target: XTarget; diff: number }): BestX => {
  if (!a) return b;
  if (b.diff < a.diff) return b;
  if (b.diff > a.diff) return a;
  const tr = xAnchorRank[b.target.anchor] - xAnchorRank[a.target.anchor];
  if (tr !== 0) return tr < 0 ? b : a;
  const sr = b.target.shapeId.localeCompare(a.target.shapeId);
  if (sr !== 0) return sr < 0 ? b : a;
  return b.target.value < a.target.value ? b : a;
};

const pickBetterY = (a: BestY, b: { target: YTarget; diff: number }): BestY => {
  if (!a) return b;
  if (b.diff < a.diff) return b;
  if (b.diff > a.diff) return a;
  const tr = yAnchorRank[b.target.anchor] - yAnchorRank[a.target.anchor];
  if (tr !== 0) return tr < 0 ? b : a;
  const sr = b.target.shapeId.localeCompare(a.target.shapeId);
  if (sr !== 0) return sr < 0 ? b : a;
  return b.target.value < a.target.value ? b : a;
};

const computeXSnap = (newBox: Bounds, staticCandidates: AlignmentGuideCandidate[], threshold: number, h: TransformHandle): BestX => {
  const affect = handleAffectsX(h);
  if (!affect) return null;
  const movingValue = affect === 'left' ? newBox.x : newBox.x + newBox.width;

  let best: BestX = null;
  staticCandidates.forEach((c) => {
    getXTargetsForCandidate(c).forEach((t) => {
      const diff = Math.abs(movingValue - t.value);
      if (diff > threshold) return;
      best = pickBetterX(best, { target: t, diff });
    });
  });
  return best;
};

const computeYSnap = (newBox: Bounds, staticCandidates: AlignmentGuideCandidate[], threshold: number, h: TransformHandle): BestY => {
  const affect = handleAffectsY(h);
  if (!affect) return null;
  const movingValue = affect === 'top' ? newBox.y : newBox.y + newBox.height;

  let best: BestY = null;
  staticCandidates.forEach((c) => {
    getYTargetsForCandidate(c).forEach((t) => {
      const diff = Math.abs(movingValue - t.value);
      if (diff > threshold) return;
      best = pickBetterY(best, { target: t, diff });
    });
  });
  return best;
};

const applyXToBox = (box: Bounds, x: BestX, h: TransformHandle): Bounds => {
  if (!x) return box;
  const affect = handleAffectsX(h);
  if (!affect) return box;

  const left = box.x;
  const right = box.x + box.width;
  const target = x.target.value;

  if (affect === 'right') {
    return { ...box, width: clampSize(target - left) };
  }
  return { ...box, x: target, width: clampSize(right - target) };
};

const applyYToBox = (box: Bounds, y: BestY, h: TransformHandle): Bounds => {
  if (!y) return box;
  const affect = handleAffectsY(h);
  if (!affect) return box;

  const top = box.y;
  const bottom = box.y + box.height;
  const target = y.target.value;

  if (affect === 'bottom') {
    return { ...box, height: clampSize(target - top) };
  }
  return { ...box, y: target, height: clampSize(bottom - target) };
};

const makeLineId = (parts: string[]): string => parts.join('|');

export class TransformSnapService {
  static compute(input: TransformSnapInput): TransformSnapOutput {
    const { oldBox, newBox, activeHandle, staticCandidates, threshold } = input;

    if (!(newBox.width > 0) || !(newBox.height > 0)) {
      return { snappedBox: newBox, snapDelta: { dx: 0, dy: 0 }, guides: [] };
    }

    if (!activeHandle || activeHandle === 'rotater') {
      return { snappedBox: newBox, snapDelta: { dx: 0, dy: 0 }, guides: [] };
    }

    const xSnap = computeXSnap(newBox, staticCandidates, threshold, activeHandle);
    const ySnap = computeYSnap(newBox, staticCandidates, threshold, activeHandle);

    const snappedBox = applyYToBox(applyXToBox(newBox, xSnap, activeHandle), ySnap, activeHandle);

    const snapDelta = {
      dx: snappedBox.x - newBox.x,
      dy: snappedBox.y - newBox.y,
    };

    const guides: AlignmentLine[] = [];
    if (xSnap) {
      guides.push({
        id: makeLineId(['transform', 'v', xSnap.target.shapeId, xSnap.target.anchor, String(xSnap.target.value)]),
        orientation: 'vertical',
        position: xSnap.target.value,
        source: { shapeId: xSnap.target.shapeId, anchor: xSnap.target.anchor },
      });
    }
    if (ySnap) {
      guides.push({
        id: makeLineId(['transform', 'h', ySnap.target.shapeId, ySnap.target.anchor, String(ySnap.target.value)]),
        orientation: 'horizontal',
        position: ySnap.target.value,
        source: { shapeId: ySnap.target.shapeId, anchor: ySnap.target.anchor },
      });
    }

    void oldBox;

    return { snappedBox, snapDelta, guides };
  }
}

